import { useState } from 'react'
import type { RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { CompactTable, EmptyTableBody, SectionTitle } from 'shared/components/section'
import { Button } from 'shared/components/ui/button'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'

/**
 * A place's journal on «Локации» (ticket 45; ADR-0045, ADR-0013): every act of an
 * administrator on it — «Включён на TezGo — Админ, 21.10 14:05», switched off (its
 * rollback, with how many Parcels it released), its drain ended, the week set,
 * closed, opened, closed for good into a recipient — newest first, as the server
 * keeps it. Read when opened; `load` is injected for the test.
 */
export type JournalEntry = RouterOutputs['locations']['Journal'][number]

const loadViaTrpc = (locationId: string) => trpcClient.locations.Journal.query({ _id: locationId })

export function LocationJournal({
	locationId,
	load = loadViaTrpc,
}: {
	locationId: string
	load?: (locationId: string) => Promise<JournalEntry[]>
}) {
	const { t } = useTranslate()
	const [entries, setEntries] = useState<JournalEntry[] | null>(null)
	const [error, setError] = useState<string | null>(null)

	const toggle = async () => {
		if (entries) {
			setEntries(null)
			return
		}
		setError(null)
		try {
			setEntries(await load(locationId))
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e))
		}
	}

	return (
		<section className='flex flex-col gap-2' data-testid='location-journal'>
			<SectionTitle
				title={t('locationJournal.title')}
				aside={
					<Button size='sm' variant='outline' onClick={toggle} data-testid='location-journal-toggle'>
						{entries ? t('locationJournal.hide') : t('locationJournal.show')}
					</Button>
				}
			/>
			{error && <p className='text-sm text-destructive'>{t('pointClosure.failed', { message: error })}</p>}
			{entries && (
				<CompactTable testId='location-journal-entries'>
					<TableHeader>
						<TableRow>
							<TableHead>{t('locationJournal.colWhat')}</TableHead>
							<TableHead>{t('locationJournal.colWho')}</TableHead>
							<TableHead>{t('locationJournal.colWhen')}</TableHead>
							<TableHead>{t('locationJournal.colDetail')}</TableHead>
						</TableRow>
					</TableHeader>
					{entries.length === 0 ? (
						<EmptyTableBody columns={4}>{t('locationJournal.empty')}</EmptyTableBody>
					) : (
						<TableBody>
							{entries.map((e) => (
								<TableRow key={e.entryId} data-kind={e.kind}>
									<TableCell className='font-medium'>{t(`locationJournal.${e.kind}`)}</TableCell>
									<TableCell>{e.by || t('locationJournal.system')}</TableCell>
									<TableCell className='text-muted-foreground tabular-nums'>{formatDayTime(e.at)}</TableCell>
									<TableCell className='text-muted-foreground whitespace-normal'>
										{[
											e.recipient &&
												t('locationJournal.recipient', { point: e.recipient.id, count: String(e.readdressed ?? 0) }),
											e.released !== null && t('locationJournal.released', { count: String(e.released) }),
											e.schedule &&
												e.schedule
													.map((d) => `${t(`weekday.${d.day}` as never)} ${d.hours.from}–${d.hours.to}`)
													.join(', '),
											e.comment,
										]
											.filter(Boolean)
											.join(' · ')}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					)}
				</CompactTable>
			)}
		</section>
	)
}
